<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.SESSIONS.2 — Sesiones: how long a sign-in stays valid for
// THIS account, and "sign everyone out" of this account (the acting
// administrator stays signed in). Other accounts of each person are untouched.
// security-center.SESSIONS.4 — shown as Próximamente while the server does not
// offer the controls (overview.session_controls false).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KaptionSecurityAPI from 'dashboard/api/kaptionSecurity';
import { useKaptionSecurity } from 'dashboard/composables/useKaptionSecurity';
import KaptionSecuritySection from '../components/KaptionSecuritySection.vue';
import KaptionSecuritySoonCard from '../components/KaptionSecuritySoonCard.vue';
import { useKaptionSecurityText } from '../useKaptionSecurityText';

const { t } = useI18n();
const currentAccount = useMapGetter('getCurrentAccount');
const { overview, setOverview, settings } = useKaptionSecurity();
const { formatWhen } = useKaptionSecurityText(overview);

const HOURS = [8, 24, 72, 168, 720];
const available = computed(() => overview.value?.session_controls === true);
const revokeRef = ref(null);
const busy = ref(false);

const lifetime = computed({
  get: () =>
    settings.value.session_max_age_hours
      ? String(settings.value.session_max_age_hours)
      : '',
  set: async value => {
    busy.value = true;
    try {
      const { data } = await KaptionSecurityAPI.updateSessions({
        session_max_age_hours: value ? Number(value) : null,
      });
      setOverview(data);
      useAlert(t('KAPTION_SECURITY.COMMON.SAVED'));
    } catch (e) {
      useAlert(t('KAPTION_SECURITY.COMMON.ERROR'));
    } finally {
      busy.value = false;
    }
  },
});

const revoke = async () => {
  busy.value = true;
  try {
    const { data } = await KaptionSecurityAPI.revokeSessions();
    setOverview(data);
    revokeRef.value?.close();
    useAlert(t('KAPTION_SECURITY.SESSIONS.REVOKED'));
  } catch (e) {
    useAlert(t('KAPTION_SECURITY.COMMON.ERROR'));
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <KaptionSecuritySection
    v-if="!available"
    :title="t('KAPTION_SECURITY.SESSIONS.TITLE')"
  >
    <KaptionSecuritySoonCard
      icon="i-lucide-timer"
      :title="t('KAPTION_SECURITY.SESSIONS.SOON_TITLE')"
      :description="t('KAPTION_SECURITY.SESSIONS.SOON_DESC')"
      data-testid="ks-sessions-soon"
    />
  </KaptionSecuritySection>
  <div v-else class="flex flex-col gap-11">
    <KaptionSecuritySection
      :title="t('KAPTION_SECURITY.SESSIONS.TITLE')"
      :description="t('KAPTION_SECURITY.SESSIONS.DESC')"
    >
      <div class="flex flex-col gap-1.5 sm:max-w-sm">
        <label for="ks-lifetime" class="text-sm font-medium text-n-slate-12">{{
          t('KAPTION_SECURITY.SESSIONS.LIFETIME_LABEL')
        }}</label>
        <select
          id="ks-lifetime"
          v-model="lifetime"
          :disabled="busy"
          class="h-10 px-3 text-sm border rounded-xl border-n-weak bg-n-alpha-black2 text-n-slate-12"
        >
          <option value="">
            {{ t('KAPTION_SECURITY.SESSIONS.LIFETIME_DEFAULT') }}
          </option>
          <option v-for="hours in HOURS" :key="hours" :value="String(hours)">
            {{ t(`KAPTION_SECURITY.SESSIONS.HOURS.${hours}`) }}
          </option>
        </select>
        <span class="text-[13px] text-n-slate-11">{{
          t('KAPTION_SECURITY.SESSIONS.LIFETIME_NOTE')
        }}</span>
      </div>
    </KaptionSecuritySection>

    <KaptionSecuritySection
      :title="t('KAPTION_SECURITY.SESSIONS.REVOKE_TITLE')"
      :description="t('KAPTION_SECURITY.SESSIONS.REVOKE_DESC')"
    >
      <div class="flex flex-wrap items-center gap-3">
        <Button
          ruby
          faded
          type="button"
          :label="t('KAPTION_SECURITY.SESSIONS.REVOKE_BUTTON')"
          data-testid="ks-revoke"
          @click="revokeRef.open()"
        />
        <span
          v-if="settings.sessions_valid_after"
          class="text-[13px] text-n-slate-11"
        >
          {{
            t('KAPTION_SECURITY.SESSIONS.LAST_REVOKE', {
              when: formatWhen(settings.sessions_valid_after),
            })
          }}
        </span>
      </div>
      <p class="m-0 text-[13px] text-n-slate-11">
        {{ t('KAPTION_SECURITY.SESSIONS.TOKENS_NOTE') }}
      </p>
    </KaptionSecuritySection>

    <Dialog
      ref="revokeRef"
      type="alert"
      :title="
        t('KAPTION_SECURITY.SESSIONS.REVOKE_CONFIRM_TITLE', {
          account: currentAccount?.name || '',
        })
      "
      :description="t('KAPTION_SECURITY.SESSIONS.REVOKE_CONFIRM_DESC')"
      :confirm-button-label="t('KAPTION_SECURITY.SESSIONS.REVOKE_BUTTON')"
      :is-loading="busy"
      @confirm="revoke"
    />
  </div>
</template>
